// Visual org chart + cost centers (Phase 2d). HR sees the reporting hierarchy as
// an indented tree and manages cost centers (create / head / headcount).
import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { Plus, Trash2, Pencil, Network, Layers, Camera } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { AG } from './theme/tokens';
import { api } from './api';

const initials = (n) => (n || '').split(' ').map((x) => x[0]).slice(0, 2).join('').toUpperCase();

function flatten(nodes, acc = []) {
  for (const n of nodes) { acc.push({ id: n.id, name: n.name }); if (n.reports?.length) flatten(n.reports, acc); }
  return acc;
}

function Node({ n, depth, dnd }) {
  const [over, setOver] = useState(false);
  const draggable = !!dnd;
  const isDragged = dnd?.draggingId === n.id;
  return (
    <div style={{ marginLeft: depth ? 22 : 0, borderLeft: depth ? `1px solid ${AG.n200}` : 'none', paddingLeft: depth ? 14 : 0 }}>
      <div
        className="flex items-center gap-2"
        draggable={draggable}
        onDragStart={draggable ? (e) => { e.stopPropagation(); dnd.setDraggingId(n.id); e.dataTransfer.effectAllowed = 'move'; } : undefined}
        onDragEnd={draggable ? () => { dnd.setDraggingId(null); setOver(false); } : undefined}
        onDragOver={draggable ? (e) => { if (dnd.draggingId && dnd.draggingId !== n.id) { e.preventDefault(); e.stopPropagation(); setOver(true); } } : undefined}
        onDragLeave={draggable ? () => setOver(false) : undefined}
        onDrop={draggable ? (e) => { e.preventDefault(); e.stopPropagation(); setOver(false); if (dnd.draggingId && dnd.draggingId !== n.id) dnd.onDrop(dnd.draggingId, n.id); dnd.setDraggingId(null); } : undefined}
        style={{ padding: '7px 10px', border: `1px solid ${over ? AG.primary : AG.n200}`, borderRadius: 10, marginBottom: 8, background: over ? AG.primaryLightest : '#fff', width: 'fit-content', minWidth: 280, cursor: draggable ? 'grab' : 'default', opacity: isDragged ? 0.4 : 1, boxShadow: over ? `0 0 0 1px ${AG.primary}` : 'none' }}
      >
        {n.avatar
          ? <img src={n.avatar} alt="" style={{ width: 30, height: 30, borderRadius: 8, objectFit: 'cover' }} />
          : <div style={{ width: 30, height: 30, borderRadius: 8, background: AG.primaryLightest, color: AG.primary, fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{initials(n.name)}</div>}
        <div style={{ lineHeight: 1.25 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5, color: AG.text }}>{n.name}{n.costCenter && <span style={{ marginLeft: 6 }}><Pill tone="info">{n.costCenter}</Pill></span>}</div>
          <div style={{ fontSize: 11.5, color: AG.n500 }}>{n.role} · {n.dept}</div>
        </div>
      </div>
      {n.reports?.map((c) => <Node key={c.id} n={c} depth={depth + 1} dnd={dnd} />)}
    </div>
  );
}

export default function OrgChart({ showToast }) {
  const [tree, setTree] = useState(null);
  const [centers, setCenters] = useState(null);
  const [edit, setEdit] = useState(null); // { id?, name, code, headId }
  const [busy, setBusy] = useState(false);
  // Versioning (Phase 2): snapshots list + which version the tree shows ('' = live).
  const [snapshots, setSnapshots] = useState([]);
  const [viewId, setViewId] = useState('');
  const [snap, setSnap] = useState(null); // loaded snapshot { name, createdAt, employeeCount, tree }
  const [capturing, setCapturing] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [overTop, setOverTop] = useState(false);

  const load = () => {
    api.orgReporting().then(setTree).catch(() => setTree([]));
    api.costCenters().then(setCenters).catch(() => setCenters([]));
    api.orgSnapshots().then(setSnapshots).catch(() => setSnapshots([]));
  };
  useEffect(() => { load(); }, []);

  // Drag-drop restructuring (1.1) — only on the live tree, never on a snapshot.
  const reassign = async (employeeId, managerId) => {
    try { await api.reassignManager(employeeId, managerId); showToast?.('Reporting line updated'); load(); }
    catch (e) { showToast?.(e.message); }
  };

  // Load the selected snapshot's frozen tree (or fall back to live).
  useEffect(() => {
    if (!viewId) { setSnap(null); return; }
    let alive = true;
    api.orgSnapshot(viewId).then((s) => alive && setSnap(s)).catch(() => alive && setViewId(''));
    return () => { alive = false; };
  }, [viewId]);

  const capture = async () => {
    if (capturing) return; setCapturing(true);
    try {
      const s = await api.captureOrgSnapshot(`Org chart — ${new Date().toLocaleDateString()}`);
      showToast?.(`Snapshot captured · ${s.employeeCount} people`);
      const list = await api.orgSnapshots(); setSnapshots(list); setViewId(s.id);
    } catch (e) { showToast?.(e.message); } finally { setCapturing(false); }
  };
  const removeSnapshot = async () => {
    if (!viewId) return;
    const id = viewId;
    try {
      await api.deleteOrgSnapshot(id);
      // Drop it locally first so a failed list refetch can't leave a phantom option.
      setSnapshots((prev) => prev.filter((s) => s.id !== id));
      setViewId('');
      api.orgSnapshots().then(setSnapshots).catch(() => {});
      showToast?.('Snapshot deleted');
    } catch (e) { showToast?.(e.message); }
  };

  // The live tree always drives the cost-center head picker; the displayed
  // reporting tree is the snapshot when one is selected.
  const people = tree ? flatten(tree) : [];
  const shownTree = viewId && snap ? snap.tree : tree;
  const save = async () => {
    if (!edit.name || !edit.code || busy) return; setBusy(true);
    try {
      if (edit.id) await api.updateCostCenter(edit.id, { name: edit.name, code: edit.code, headId: edit.headId || null });
      else await api.addCostCenter({ name: edit.name, code: edit.code, headId: edit.headId || null });
      showToast?.('Cost center saved'); setEdit(null); load();
    } catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const remove = async (c) => { try { await api.deleteCostCenter(c.id); load(); } catch (e) { showToast?.(e.message); } };

  if (!tree || !centers) return <CenterSpinner />;

  return (
    <div className="space-y-4 fade-up">
      <SectionTitle title="Org Chart" sub="Reporting hierarchy & cost centers" />

      {/* Cost centers */}
      <Card style={{ padding: 18 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
          <div className="flex items-center gap-2"><Layers size={17} style={{ color: AG.primary }} /><span style={{ fontWeight: 600, color: AG.text }}>Cost centers</span></div>
          <OutlineBtn onClick={() => setEdit({ name: '', code: '', headId: '' })}><Plus size={14} style={{ marginRight: 4 }} /> Add</OutlineBtn>
        </div>
        {centers.length === 0 ? <p style={{ fontSize: 13, color: AG.n500 }}>No cost centers yet. Select a specific entity to add one.</p> : (
          <div style={{ display: 'grid', gap: 8 }}>
            {centers.map((c) => (
              <div key={c.id} className="flex items-center justify-between" style={{ border: `1px solid ${AG.n200}`, borderRadius: 10, padding: '10px 12px' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: AG.text }}>{c.code} · {c.name}</div>
                  <div style={{ fontSize: 12, color: AG.n500 }}>{c.headName ? `Head: ${c.headName}` : 'No head'} · {c.headcount} {c.headcount === 1 ? 'member' : 'members'}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setEdit({ id: c.id, name: c.name, code: c.code, headId: c.headId || '' })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4 }}><Pencil size={15} /></button>
                  <button onClick={() => remove(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4 }}><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Reporting tree (live or a saved snapshot) */}
      <Card style={{ padding: 18 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
          <div className="flex items-center gap-2"><Network size={17} style={{ color: AG.primary }} /><span style={{ fontWeight: 600, color: AG.text }}>Reporting hierarchy</span></div>
          <div className="flex items-center gap-2">
            <select value={viewId} onChange={(e) => setViewId(e.target.value)} className="ag-input" style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }} title="View the current chart or a saved snapshot">
              <option value="">Current (live)</option>
              {snapshots.map((s) => <option key={s.id} value={s.id}>{s.name} · {new Date(s.createdAt).toLocaleDateString()}</option>)}
            </select>
            <OutlineBtn onClick={capture} disabled={capturing}><Camera size={14} style={{ marginRight: 5 }} /> {capturing ? 'Capturing…' : 'Capture now'}</OutlineBtn>
          </div>
        </div>
        {viewId && snap && (
          <div className="flex items-center justify-between" style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 8, background: AG.primaryLightest, color: AG.primaryDark, fontSize: 12.5, gap: 8, flexWrap: 'wrap' }}>
            <span>📌 Viewing snapshot <b>{snap.name}</b> · captured {new Date(snap.createdAt).toLocaleString()} · {snap.employeeCount} people</span>
            <button onClick={removeSnapshot} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.error, fontSize: 12.5, fontWeight: 600 }}>Delete</button>
          </div>
        )}
        {!viewId && (
          <div
            onDragOver={(e) => { if (draggingId) { e.preventDefault(); setOverTop(true); } }}
            onDragLeave={() => setOverTop(false)}
            onDrop={(e) => { e.preventDefault(); setOverTop(false); if (draggingId) reassign(draggingId, null); setDraggingId(null); }}
            style={{ fontSize: 12.5, color: overTop ? AG.primaryDark : AG.n500, marginBottom: 12, padding: '8px 12px', borderRadius: 8, border: `1px dashed ${overTop ? AG.primary : AG.n200}`, background: overTop ? AG.primaryLightest : AG.n50 }}
          >
            ↳ <b>Drag</b> a person onto their new manager to restructure — or drop here to move them to the <b>top level</b>.
          </div>
        )}
        {!shownTree || shownTree.length === 0 ? <p style={{ fontSize: 13, color: AG.n500 }}>{viewId ? 'This snapshot has no employees.' : 'No employees in scope.'}</p> : (
          <div style={{ overflowX: 'auto' }}>{shownTree.map((n) => <Node key={n.id} n={n} depth={0} dnd={!viewId ? { draggingId, setDraggingId, onDrop: reassign } : undefined} />)}</div>
        )}
      </Card>

      {/* What-if reorg scenarios (1.1) */}
      <ReorgScenarios people={people} showToast={showToast} onApplied={load} />

      <Dialog open={!!edit} onClose={() => !busy && setEdit(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{edit?.id ? 'Edit cost center' : 'Add cost center'}</DialogTitle>
        <DialogContent>
          {edit && (
            <div style={{ paddingTop: 6 }}>
              <div className="grid grid-cols-2 gap-2" style={{ marginBottom: 12 }}>
                <TextField size="small" label="Code" value={edit.code} onChange={(e) => setEdit({ ...edit, code: e.target.value })} />
                <TextField size="small" label="Name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
              </div>
              <label style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Head (optional)</label>
              <select value={edit.headId} onChange={(e) => setEdit({ ...edit, headId: e.target.value })} className="ag-input" style={{ marginTop: 4 }}>
                <option value="">— None —</option>
                {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setEdit(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={save} disabled={busy}>Save</PrimaryBtn></DialogActions>
      </Dialog>
    </div>
  );
}

// What-if reorganization scenarios (1.1) — draft a set of manager moves, preview
// the projected structure + span-of-control impact, then apply or discard. Live
// reporting lines are untouched until a scenario is applied.
function ReorgScenarios({ people, showToast, onApplied }) {
  const [list, setList] = useState(null);
  const [active, setActive] = useState(null); // loaded scenario detail
  const [name, setName] = useState('');
  const [move, setMove] = useState({ employeeId: '', managerId: '' });
  const [busy, setBusy] = useState(false);
  const reqToken = useRef(0);
  const load = () => api.reorgScenarios().then(setList).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  // Guard against an open/open race — only the latest fetch may set `active`.
  const open = async (id) => { const my = ++reqToken.current; try { const d = await api.reorgScenario(id); if (reqToken.current === my) setActive(d); } catch (e) { showToast?.(e.message); } };
  const refresh = () => active && open(active.id);

  const create = async () => {
    if (!name.trim() || busy) return; setBusy(true);
    try { const s = await api.createReorgScenario(name.trim()); setName(''); await load(); open(s.id); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const addMove = async () => {
    if (!move.employeeId || busy) return; setBusy(true);
    try { await api.setReorgMove(active.id, move.employeeId, move.managerId || null); setMove({ employeeId: '', managerId: '' }); refresh(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const dropMove = async (employeeId) => { try { await api.removeReorgMove(active.id, employeeId); refresh(); } catch (e) { showToast?.(e.message); } };
  const apply = async () => {
    if (busy) return; setBusy(true);
    try { const r = await api.applyReorgScenario(active.id); showToast?.(`Applied ${r.applied} move(s)`); setActive(null); load(); onApplied?.(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const discard = async (id) => { try { await api.deleteReorgScenario(id); if (active?.id === id) setActive(null); load(); } catch (e) { showToast?.(e.message); } };

  if (!list) return null;
  return (
    <Card style={{ padding: 18 }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div className="flex items-center gap-2"><Layers size={17} style={{ color: AG.primary }} /><span style={{ fontWeight: 600, color: AG.text }}>What-if reorg scenarios</span></div>
        <div className="flex items-center gap-2">
          <TextField size="small" placeholder="New scenario name" value={name} onChange={(e) => setName(e.target.value)} />
          <PrimaryBtn onClick={create} disabled={busy || !name.trim()}><Plus size={14} style={{ marginRight: 4 }} /> Create</PrimaryBtn>
        </div>
      </div>
      <p style={{ fontSize: 12.5, color: AG.n500, margin: '0 0 12px' }}>Model a reorganization without touching live data — preview the impact, then apply it in one click.</p>

      {list.length > 0 && (
        <div className="flex flex-wrap gap-2" style={{ marginBottom: active ? 14 : 0 }}>
          {list.map((s) => (
            <div key={s.id} className="flex items-center gap-2" style={{ padding: '6px 10px', border: `1px solid ${active?.id === s.id ? AG.primary : AG.n200}`, borderRadius: 8 }}>
              <button onClick={() => open(s.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.text, fontSize: 13, fontWeight: 600 }}>{s.name}</button>
              <Pill tone={s.status === 'Applied' ? 'success' : s.status === 'Discarded' ? 'neutral' : 'info'}>{s.status} · {s.moveCount}</Pill>
              {s.status === 'Draft' && <button onClick={() => discard(s.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400 }}><Trash2 size={13} /></button>}
            </div>
          ))}
        </div>
      )}

      {active && (
        <div style={{ borderTop: `1px solid ${AG.divider}`, paddingTop: 14 }}>
          <div className="flex items-center justify-between" style={{ marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontWeight: 600, color: AG.text }}>{active.name} <Pill tone={active.valid ? 'success' : 'error'}>{active.valid ? 'valid' : 'has a cycle'}</Pill></span>
            {active.status === 'Draft' && <PrimaryBtn onClick={apply} disabled={busy || !active.moves.length || !active.valid}>Apply {active.moves.length} move(s)</PrimaryBtn>}
          </div>

          {active.status === 'Draft' && (
            <div className="flex items-center gap-2" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
              <select className="ag-input" style={{ width: 'auto' }} value={move.employeeId} onChange={(e) => setMove({ employeeId: e.target.value, managerId: '' })}>
                <option value="">Move…</option>
                {people.filter((p) => !active.moves.some((m) => m.employeeId === p.id)).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <span style={{ fontSize: 13, color: AG.n500 }}>under</span>
              <select className="ag-input" style={{ width: 'auto' }} value={move.managerId} onChange={(e) => setMove({ ...move, managerId: e.target.value })}>
                <option value="">— Top level —</option>
                {people.filter((p) => p.id !== move.employeeId).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <OutlineBtn onClick={addMove} disabled={busy || !move.employeeId}>Add move</OutlineBtn>
            </div>
          )}

          {active.moves.length === 0 ? <p style={{ fontSize: 13, color: AG.n500 }}>No moves yet. Add a move above to model the change.</p> : (
            <div style={{ display: 'grid', gap: 6 }}>
              {active.moves.map((m) => (
                <div key={m.employeeId} className="flex items-center gap-2" style={{ padding: '7px 10px', border: `1px solid ${AG.divider}`, borderRadius: 8 }}>
                  <span style={{ fontSize: 13, color: AG.text }}><b>{m.employeeName}</b> → {m.newManagerName || '(top level)'}</span>
                  {active.status === 'Draft' && <button onClick={() => dropMove(m.employeeId)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, marginLeft: 'auto' }}><Trash2 size={14} /></button>}
                </div>
              ))}
            </div>
          )}

          {active.impact?.spanChanges?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 6 }}>Span-of-control impact · {active.impact.moved} person/people moved</div>
              <div className="flex flex-wrap gap-2">
                {active.impact.spanChanges.map((c) => {
                  const mgr = people.find((p) => p.id === c.managerId);
                  return <Pill key={c.managerId} tone={c.delta > 0 ? 'success' : 'warning'}>{mgr?.name || c.managerId}: {c.before}→{c.after}</Pill>;
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
